import { Body, Controller, Delete, Get, HttpCode, Param, Patch, Post, Req, UseGuards } from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiParam,
  ApiResponse,
  ApiSecurity,
  ApiTags,
} from '@nestjs/swagger';
import { AddressListResponse, AddressSummary, CreateAddressRequest, UpdateAddressRequest } from '@cart/shared-types';
import { AuthenticatedRequest, AuthGuard } from '../auth/auth.guard';
import { AddressesService } from './addresses.service';

const addressSchema = {
  type: 'object',
  required: ['label', 'recipientName', 'line1', 'city', 'state', 'postalCode', 'country'],
  properties: {
    label: { type: 'string', example: 'Home' },
    recipientName: { type: 'string', example: 'Jane Doe' },
    line1: { type: 'string', example: '123 Main St' },
    line2: { type: 'string' },
    city: { type: 'string', example: 'Austin' },
    state: { type: 'string', example: 'TX' },
    postalCode: { type: 'string', example: '78701' },
    country: { type: 'string', example: 'US' },
    phone: { type: 'string' },
  },
};

@ApiTags('Addresses')
@ApiBearerAuth()
@ApiSecurity('store-id')
@Controller('addresses')
@UseGuards(AuthGuard)
export class AddressesController {
  constructor(private readonly addressesService: AddressesService) {}

  @Get()
  @ApiOperation({ summary: 'List saved addresses for the current user' })
  @ApiResponse({ status: 200, description: 'Returns address list' })
  list(@Req() request: AuthenticatedRequest): Promise<AddressListResponse> {
    return this.addressesService.list(request.user?.id ?? '');
  }

  @Post()
  @ApiOperation({ summary: 'Save a new address' })
  @ApiBody({ schema: addressSchema })
  @ApiResponse({ status: 201, description: 'Address saved' })
  create(
    @Req() request: AuthenticatedRequest,
    @Body() body: CreateAddressRequest
  ): Promise<AddressSummary> {
    return this.addressesService.create(request.user?.id ?? '', body);
  }

  @Patch(':id')
  @ApiOperation({ summary: 'Update a saved address' })
  @ApiParam({ name: 'id', type: String })
  @ApiBody({ schema: addressSchema })
  @ApiResponse({ status: 200, description: 'Address updated' })
  @ApiResponse({ status: 404, description: 'Address not found' })
  update(
    @Req() request: AuthenticatedRequest,
    @Param('id') addressId: string,
    @Body() body: UpdateAddressRequest
  ): Promise<AddressSummary> {
    return this.addressesService.update(request.user?.id ?? '', addressId, body);
  }

  @Delete(':id')
  @HttpCode(204)
  @ApiOperation({ summary: 'Delete a saved address' })
  @ApiParam({ name: 'id', type: String })
  @ApiResponse({ status: 204, description: 'Address deleted' })
  @ApiResponse({ status: 404, description: 'Address not found' })
  delete(@Req() request: AuthenticatedRequest, @Param('id') addressId: string): Promise<void> {
    return this.addressesService.delete(request.user?.id ?? '', addressId);
  }
}
